import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-40b974e6"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "chart-container" }

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'

import * as echarts from 'echarts'
import { ResizeService } from '@/services/resize.service'
import { getAssignPieOption } from '@/utils/echarts-option/pie'
import { getChartPiePrimaryPalette } from '@/utils/chart-theme-colors'
import { useEchartsThemeSync } from '@/composables/use-echarts-theme-sync'
import Card from '@/views/dashboard/_components/card/card.vue'
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue'
import PieCenter from '@/views/dashboard/_components/echarts/pie-center.vue'

interface ChartDataItem {
  name: string
  value: number
  /** 与 /client_find/statistics 中 system_distribution.system_type 一致，用于筛选已发现列表 */
  system_type?: number
}


export default /*#__PURE__*/_defineComponent({
  __name: 'system-distribution-chart',
  props: {
    data: { default: () => [] }
  },
  emits: ["sliceClick"],
  setup(__props: any, { emit: __emit }) {

const { t, locale } = useI18n()
const emit = __emit

const props = __props

const injector = useReflectiveInjector([ResizeService])
const resizeService = injector.get(ResizeService)

const chartRef = ref<HTMLElement>()
let chartInstance: echarts.EChartsType | null = null

// 深蓝色主题色系，以 var(--el-color-primary) 为主
const defaultColors = computed(() => getChartPiePrimaryPalette())

// 计算TOP10数据
const top10Data = computed(() => {
  if (!props.data?.length) return []
  return [...props.data].sort((a, b) => b.value - a.value).slice(0, 10)
})

const chartData = computed(() => {
  return top10Data.value.map((item, index) => ({
    ...item,
    color: item.color || defaultColors.value[index % defaultColors.value.length]
  }))
})

const totalCount = computed(() =>
  chartData.value.reduce((sum, item) => sum + item.value, 0)
)

const hasData = computed(() => totalCount.value > 0)

function getChartData() {
  return chartData.value.map(item => ({
    value: item.value,
    name: item.name,
    system_type: item.system_type,
    itemStyle: {
      color: item.color,
    },
  }))
}

function bindChartEvents() {
  if (!chartInstance) return
  chartInstance.off('click')
  chartInstance.on('click', (params: any) => {
    const data = params?.data
    const name = (data?.name ?? params?.name) as string
    const system_type = data?.system_type as number | undefined
    const raw = data?.value ?? params?.value
    const value = typeof raw === 'number' ? raw : Number(raw) || 0
    emit('sliceClick', { name, system_type, value })
  })
}

function createChart() {
  if (!chartRef.value) return
  chartInstance = echarts.init(chartRef.value)
  bindChartEvents()
}

function getChartOption() {
  const option = {
    series: [{
      name: t('systemDistChart.title'),
      cursor: 'pointer',
      data: getChartData(),
    }],
  }
  return getAssignPieOption(option, { unit: t('systemDistChart.unit') })
}

function renderChart() {
  if (!chartInstance) return
  chartInstance.setOption(getChartOption())
}

function initChart() {
  chartInstance?.dispose()
  createChart()
  renderChart()
}

const resizeSubscribe = resizeService.resizeSubject.subscribe(() => {
  chartInstance?.resize()
})

watch(() => props.data, () => {
  if (!hasData.value) return
  if (!chartInstance) {
    nextTick(initChart)
  } else {
    renderChart()
  }
}, { deep: true })

/** ECharts 选项里的 t() 不会随语言自动更新，需监听 locale 后重新 setOption */
watch(locale, () => {
  if (!hasData.value) return
  if (!chartInstance) {
    nextTick(initChart)
  } else {
    renderChart()
  }
})

useEchartsThemeSync(() => {
  if (chartInstance && hasData.value) renderChart()
})

onMounted(() => {
  if (hasData.value) {
    setTimeout(() => initChart(), 100)
  }
})

onUnmounted(() => {
  resizeSubscribe.unsubscribe()
  chartInstance && chartInstance.dispose()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Card, {
    size: "second",
    title: _ctx.$t('systemDistChart.title'),
    class: "system-distribution-chart-wrap chart-card"
  }, {
    default: _withCtx(() => [
      _withDirectives(_createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          ref_key: "chartRef",
          ref: chartRef,
          class: "chart"
        }, null, 512),
        _createVNode(PieCenter, {
          title: _ctx.$t('systemDistChart.totalCount'),
          value: totalCount.value,
          unit: _ctx.$t('systemDistChart.unit')
        }, null, 8, ["title", "value", "unit"])
      ], 512), [
        [_vShow, hasData.value]
      ]),
      _withDirectives(_createVNode(EmptyData, {
        height: "100%",
        "bg-color": "transparent"
      }, null, 512), [
        [_vShow, !hasData.value]
      ])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})